import { saveSetupProgress } from '../../../../api/stays-setup-client';
import type { ReadinessItem, SetupStepKey } from '../../../../api/stays-setup-client';
import { w } from '../../../../stays/wording';

/**
 * THE HOST SETUP WIZARD'S STEPS (Independent Stays plan ISR, R-ISR.6).
 *
 * The owner, 2026-10-27: "it should be a nice wizard pipeline that will guide
 * me through the steps of creating my own stays". One step per screen; each
 * says what it is for and what comes next. The keys mirror the server's
 * SETUP_STEPS (BE src/stays/routes/setup.js); IS2 (properties) and IS3
 * (signup) plug into step 1 later, they never add a second flow.
 */
export interface SetupStep {
  key: SetupStepKey;
  title: string;
  /** One line: why this matters. */
  why: string;
  /** Optional steps can be skipped without a warning. */
  optional?: boolean;
}

export const SETUP_STEPS: ReadonlyArray<SetupStep> = [
  {
    key: 'unit',
    title: 'Which unit',
    why: 'Pick the home guests will stay in. Its building must have Stays switched on.',
  },
  {
    key: 'basics',
    title: 'Listing basics',
    why: 'The name guests see, when they can arrive and leave, and how many it sleeps.',
  },
  {
    key: 'rules',
    title: 'House rules',
    why: 'Guests read and sign these during check-in. Write them in your own words; the summary is shown before they sign.',
  },
  {
    key: 'arrival',
    // Not just "Arrival": the embedded guide editor has its own "Arrival" section heading.
    title: 'Arrival and door access',
    why: 'How guests find the home and get in. Door codes are shown only after check-in is complete.',
  },
  {
    key: 'calendars',
    title: 'Booking sites',
    why: 'Paste your Airbnb or Booking.com calendar links so stays arrive by themselves.',
    optional: true,
  },
  {
    key: 'first_stay',
    title: 'Your first stay',
    why: 'Add a stay by hand: the dates and the guest who is coming.',
    optional: true,
  },
  {
    key: 'send_link',
    title: 'Send the check-in link',
    why: 'See the guest page as your guest will, then send them their link.',
  },
];

export const STEP_KEYS: ReadonlyArray<SetupStepKey> = SETUP_STEPS.map(s => s.key);

/**
 * The steps as one organisation reads them (Independent Stays IS2). Without
 * Lobbero Desk step 1 is "Your property": the host adds the place itself
 * (a whole place, an apartment, several apartments) instead of picking a unit
 * of a building. Same key, same order — the server's progress is unchanged.
 */
export const setupSteps = (desk: boolean): ReadonlyArray<SetupStep> =>
  desk
    ? SETUP_STEPS
    : SETUP_STEPS.map(s =>
        s.key === 'unit' ? { ...s, title: w('setupFirstStepTitle', false), why: w('setupFirstStepWhy', false) } : s
      );

export const stepIndex = (key: SetupStepKey): number => STEP_KEYS.indexOf(key);

export const isStepKey = (v: unknown): v is SetupStepKey =>
  typeof v === 'string' && (STEP_KEYS as ReadonlyArray<string>).includes(v);

/** The step after `key`, or null after the last. */
export const nextStep = (key: SetupStepKey): SetupStepKey | null => STEP_KEYS[stepIndex(key) + 1] || null;

/** The step before `key`, or null before the first. */
export const prevStep = (key: SetupStepKey): SetupStepKey | null => {
  const i = stepIndex(key);

  return i > 0 ? STEP_KEYS[i - 1] : null;
};

/**
 * Where the wizard opens: the asked step when it is a real one; with no
 * listing, step 1; else the saved step, else the first step whose readiness
 * items are not all done (unit is always done once a listing exists).
 */
export function openingStep(
  asked: string | null,
  hasListing: boolean,
  saved: SetupStepKey | null,
  items: ReadonlyArray<ReadinessItem>
): SetupStepKey {
  if (!hasListing) return 'unit';
  if (isStepKey(asked) && asked !== 'unit') return asked;
  if (saved && saved !== 'unit') return saved;
  const missing = STEP_KEYS.find(k => k !== 'unit' && items.some(i => i.step === k && !i.done));

  return missing || 'basics';
}

/** A step is ticked when every readiness item it fixes is done, or the host completed it. */
export function stepDone(key: SetupStepKey, items: ReadinessItem[], completed: ReadonlyArray<SetupStepKey>, hasListing: boolean): boolean {
  if (key === 'unit') return hasListing;
  const own = items.filter(i => i.step === key);
  if (own.length) return own.every(i => i.done);

  return completed.includes(key);
}

/**
 * After a unit is promoted (in the guide's step 1 or from a unit page): steps 1
 * and 2 are done, so save that and open the guide at "House rules". Saving the
 * place is a convenience: the guide opens even when it fails.
 */
export async function openSetupAfterPromote(navigate: (to: string) => void, listingId: string): Promise<void> {
  try {
    await saveSetupProgress(listingId, { step: 'rules', completed: ['unit', 'basics'] });
  } catch {
    /* the asked step still opens */
  }
  navigate(`/dashboard/stays/setup/${listingId}?step=rules`);
}
